/**
 * Constantes globales del juego. Centralizar estos valores evita "numeros magicos"
 * dispersos por el codigo y facilita ajustar el look & feel desde un solo lugar.
 */

export const GAME_TITLE = 'Maia Eyekon';

/** Resolucion logica de diseño base (paisaje). Se usa como referencia/fallback. */
export const GAME_WIDTH = 1280;
export const GAME_HEIGHT = 720;

/**
 * Lado corto del espacio de diseño: el lienzo siempre mantiene este tamaño en su
 * dimension mas corta, sin importar el dispositivo. Asi las tipografias y los
 * elementos conservan un tamaño consistente en celular, tablet y PC.
 */
export const DESIGN_SHORT_SIDE = 720;

/**
 * Proporcion maxima permitida del lado largo respecto del corto. Evita disenos
 * extremadamente alargados; en pantallas muy anchas/altas deja bandas minimas.
 */
export const MAX_DESIGN_ASPECT = 2.4;

/**
 * Calcula el tamaño del lienzo de diseño en funcion del viewport real para que el
 * juego ocupe toda la pantalla (sin bandas) y mantenga la UI a escala consistente.
 * En vertical fija el ancho a DESIGN_SHORT_SIDE; en horizontal fija el alto.
 */
export function computeDesignSize(
  viewportWidth: number,
  viewportHeight: number,
): { width: number; height: number } {
  const width = Math.max(1, viewportWidth);
  const height = Math.max(1, viewportHeight);

  if (width >= height) {
    const aspect = Math.min(width / height, MAX_DESIGN_ASPECT);
    return { width: Math.round(DESIGN_SHORT_SIDE * aspect), height: DESIGN_SHORT_SIDE };
  }

  const ratio = Math.min(height / width, MAX_DESIGN_ASPECT);
  return { width: DESIGN_SHORT_SIDE, height: Math.round(DESIGN_SHORT_SIDE * ratio) };
}

/** Claves de las escenas. Usar constantes evita errores de tipeo al navegar entre escenas. */
export const SceneKeys = {
  Boot: 'BootScene',
  Preload: 'PreloadScene',
  Hub: 'HubScene',
  CharacterSelect: 'CharacterSelectScene',
} as const;

export type SceneKey = (typeof SceneKeys)[keyof typeof SceneKeys];

/** Paleta de colores del juego (estilo K-pop: vibrante y luminoso). */
export const Palette = {
  background: 0x1a1033,
  backgroundAlt: 0x2d1b4e,
  primary: 0xff5fa2,
  secondary: 0x7c4dff,
  accent: 0x4dd0e1,
  highlight: 0xffd54f,
  textLight: '#ffffff',
  textDark: '#1a1033',
} as const;

/** Clave usada para persistir el progreso del jugador en el almacenamiento local. */
export const SAVE_KEY = 'maia-eyekon:player-profile:v1';

/** Textura del fondo de escenario compartido por los menus. */
export const STAGE_BG_TEXTURE = 'bg-stage';
export const STAGE_BG_FILE = 'bg-stage.png';
